import { type CanvasRetrieve } from '@openapi'
import type {
  UiAppResizeRequest,
  UiCanvasNavigatePageRequest,
  UiUpdateCanvasRequest,
  UiSelectCanvasesRequest,
  UiSelectCanvasesResponse,
} from '@sdk/api/modules/ui/types.ui'
import { UI_API_METHOD_TYPES } from '@sdk/api/modules/ui/types.ui'
import { usePostRobot } from '@lib/sdk/utils/postrobot'

/**
 * Modify the currently loaded canvas. On the web it sends
 * an [update_canvas](https://pitcher.readme.io/reference/update_canvas) request to the server.
 *
 * @param payload parts of the canvas object to update with fields to return
 */
export async function updateCanvas(payload: UiUpdateCanvasRequest): Promise<CanvasRetrieve> {
  const pr = await usePostRobot()
  return pr
    .send(window.parent, UI_API_METHOD_TYPES.UI_UPDATE_CANVAS, payload)
    .then((response) => response.data as CanvasRetrieve)
}

/**
 * Notify the CatalogIQ instance that the app is loaded and ready to get the data. Triggering this method will trigger
 * the `on_app_set_data` event.
 *
 * @example
 * @example
 * useUi().appLoaded()
 */
export async function appLoaded(): Promise<void> {
  window.parent.postMessage({ type: UI_API_METHOD_TYPES.UI_APP_LOADED, body: {} }, '*')
}

/**
 * Resize the app iframe to a given height.
 *
 * Note that initial iframe height comes from the `app.json` manifest file. It can also be adjusted by the user when adding
 * app component to the canvas. In case when the app must be temporarily resized to show larger ui elements it can read the
 * user-set height from the [on_app_set_data](#on-app-set-data) event.
 * @param payload object containing requested height of the iframe as the number of pixels
 */
export async function appResize(payload: UiAppResizeRequest): Promise<void> {
  window.parent.postMessage({ type: UI_API_METHOD_TYPES.UI_APP_RESIZE, body: payload }, '*')
}

/**
 * Close the Canvas Section Execution modal. Only takes effect for `canvas_section_execution` app modules
 *
 * Note that this method simply closes the modal and will result in the app calling it being unmounted from the DOM and
 * destroyed. Make sure your app has finished updating data before you call this method.
 *
 * @example
 * useUi().closeCanvasSectionExecution()
 */
export async function closeCanvasSectionExecution(): Promise<void> {
  window.parent.postMessage({ type: UI_API_METHOD_TYPES.UI_CLOSE_CANVAS_SECTION_EXECUTION, body: {} }, '*')
}

/**
 * Navigate to the next page in the current canvas. Noop if the current page is the last one.
 *
 * @example
 * useUi().canvasNavigateNextPage()
 */
export async function canvasNavigateNextPage(): Promise<void> {
  const pr = await usePostRobot()
  pr.send(window.parent, UI_API_METHOD_TYPES.UI_CANVAS_NAVIGATE_NEXT_PAGE)
}

/**
 * Navigate to the previous page in the current canvas. Noop if the current page is the first one.
 *
 * @example
 * useUi().canvasNavigatePreviousPage()
 */
export async function canvasNavigatePreviousPage(): Promise<void> {
  const pr = await usePostRobot()
  pr.send(window.parent, UI_API_METHOD_TYPES.UI_CANVAS_NAVIGATE_PREVIOUS_PAGE)
}

/**
 * Navigate to a specific canvas place in the current canvas. Despite the event and parameter name, it takes the component
 * id that we want to put in view.
 *
 * @example
 * const uiApi = useUi() // our useApi()
 *
 * let activeCanvas = null
 * const unsubscribe = uiApi.on_app_set_data((data) => {
 *   activeCanvas = data?.canvas
 * })
 *
 * document.getElementById('my-button').addEventListener('click', () => {
 *   const target = activeCanvas?.content?.data?.find((i: any) => i.data.app_name === 'my-target-app-id') || null
 *   if (target) {
 *     uiApi.canvasNavigatePage({ page: target.id })
 *   }
 * })
 */
export async function canvasNavigatePage(payload: UiCanvasNavigatePageRequest): Promise<void> {
  const pr = await usePostRobot()
  pr.send(window.parent, UI_API_METHOD_TYPES.UI_CANVAS_NAVIGATE_PAGE, payload)
}

/**
 * Opens a modal to view a canvas by ID.
 *
 * ```
 * onMounted(() => {
 *   uiApi.openCanvasOverlay({
 *     id: '01HH4RCBH631K4JDHWAQB0RPR6',
 *     edit_mode: false,
 *     fullscreen: true,
 *   })
 * })
 * ```
 *
 * You can also specify a position for the canvas overlay:
 *
 * ```
 * uiApi.openCanvasOverlay({
 *   id: '01HH4RCBH631K4JDHWAQB0RPR6',
 *   position: {
 *     top: '10px',
 *     left: '20px',
 *     right: '20px',
 *     bottom: '10px'
 *   }
 * })
 * ```
 */
export async function openCanvasOverlay(payload: {
  id: CanvasRetrieve['id']
  edit_mode?: boolean
  fullscreen?: boolean
  component_id?: string
  section_id?: string
  position?: {
    left?: number | string
    right?: number | string
    top?: number | string
    bottom?: number | string
  }
}): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, UI_API_METHOD_TYPES.UI_OPEN_CANVAS_OVERLAY, payload)
}

/**
 * Allows the user to select (and preselect) canvases from the instance.
 *
 * This method allows you to prompt the user to select canvases from the CatalogIQ
 * instance and use that canvas selection in your application.
 *
 * @example
 * const api = useApi() // or useUi() if you know that you 're in UI context
 * api
 *   .selectCanvases({
 *     allowed_types: ['canvas','canvas-template','section','section-template'] // default ['canvas']
 *     selections: [
 *       { id: "01HCZ623YYRFJQ0F7B69VWE510" },
 *       { id: "02HCZ623YYRFJQ0F7B69VWE510" },
 *     ], // default []
 *   })
 *
 * @param payload optional payload to preselect specific canvases
 * @returns Promise with the user action and selected canvases
 */
export async function selectCanvases(payload: UiSelectCanvasesRequest = {}): Promise<UiSelectCanvasesResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_SELECT_CANVASES, payload)
  return <UiSelectCanvasesResponse>response.data
}
